import React, { useCallback, useEffect, useMemo, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

// Banquet Demand Forecast — surfaces the rule-engine output shipped in
// backend/v1/routes/banquetIntelligence.js GET /demand-forecast (Wave 8d Rust
// forecaster). Mirrors the look-and-feel of PmsOccupancyForecast.js so the
// site has one consistent forecast pattern.

const sectionStyle = {
  background: 'var(--theme-bg-light, #fff)',
  border: '1px solid var(--theme-border, #e5e7eb)',
  borderRadius: 8,
  padding: '1rem 1.2rem',
  marginTop: '1rem',
};

const kpiCardStyle = {
  background: '#fff',
  border: '1px solid #e5e7eb',
  borderRadius: 8,
  padding: '0.7rem 0.9rem',
  minWidth: 140,
};

const demandColor = (level) => {
  switch (String(level || '').toUpperCase()) {
    case 'HIGH': return { bg: '#dcfce7', border: '#86efac', text: '#166534' };
    case 'MEDIUM': return { bg: '#fef9c3', border: '#fde047', text: '#854d0e' };
    case 'LOW': return { bg: '#fee2e2', border: '#fca5a5', text: '#991b1b' };
    default: return { bg: '#f3f4f6', border: '#e5e7eb', text: '#6b7280' };
  }
};

const fmtCurrency = (n) => {
  if (n == null) return '-';
  return `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};

const fmtNum = (n) => (n == null ? '-' : Number(n).toLocaleString('en-IN'));

const fmtDateLong = (yyyymmdd) => {
  if (!yyyymmdd) return '-';
  const d = new Date(`${yyyymmdd}T00:00:00`);
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

function BanquetDemandForecast({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [horizon, setHorizon] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [selectedCell, setSelectedCell] = useState(null);

  const headers = useCallback(() => ({
    Authorization: `Bearer ${token}`,
    'x-chain-id': selectedProperty?.chain_id || '',
    'x-property-id': selectedProperty?.id || '',
  }), [token, selectedProperty]);

  const load = useCallback(async (h) => {
    if (!selectedProperty?.id) return;
    setLoading(true);
    setError('');
    setSelectedCell(null);
    try {
      const res = await fetch(`/api/v1/banquet-intelligence/demand-forecast?days=${h || horizon}`, { headers: headers() });
      const body = await res.json();
      if (!res.ok) throw new Error(body?.error || 'Failed to load forecast');
      setData(body);
    } catch (err) {
      setError(err?.message || 'Failed to load forecast');
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedProperty?.id, horizon, headers]);

  useEffect(() => { load(horizon); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [selectedProperty?.id]);

  const summary = data?.summary || null;
  const forecast = useMemo(() => Array.isArray(data?.forecast) ? data.forecast : [], [data]);

  // Group forecast days by month for a calendar-style render.
  const calendarMonths = useMemo(() => {
    const map = new Map();
    forecast.forEach((d) => {
      const ym = String(d.date || '').slice(0, 7);
      if (!ym) return;
      if (!map.has(ym)) map.set(ym, []);
      map.get(ym).push(d);
    });
    return Array.from(map.entries()).map(([ym, days]) => ({ ym, days }));
  }, [forecast]);

  const trendBadge = (() => {
    if (!summary) return null;
    const m = String(summary.trend_momentum || '').toUpperCase();
    if (m === 'RISING') return { label: 'Search demand rising', color: '#166534', bg: '#dcfce7' };
    if (m === 'FALLING') return { label: 'Search demand falling', color: '#991b1b', bg: '#fee2e2' };
    return { label: 'Search demand steady', color: '#6b7280', bg: '#f3f4f6' };
  })();

  if (!selectedProperty?.id) {
    return (
      <div style={{ padding: '1rem', maxWidth: 1100 }}>
        <h2 style={{ marginTop: 0, color: 'var(--theme-primary)' }}>Banquet Demand Forecast</h2>
        <p style={{ color: 'var(--theme-text-muted, #6b7280)' }}>Please select a property to view the forecast.</p>
      </div>
    );
  }

  return (
    <div style={{ padding: '1rem', maxWidth: 1200 }}>
      {/* Header + horizon tabs */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.6rem', marginBottom: '0.6rem' }}>
        <div>
          <h2 style={{ margin: 0, color: 'var(--theme-primary)' }}>Banquet Demand Forecast</h2>
          <p style={{ margin: 0, color: 'var(--theme-text-secondary, #6b7280)', fontSize: '0.85rem' }}>
            Predicted demand by date — uses your booking history, local events, and search trends
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.4rem' }}>
          {[30, 60, 90].map((h) => (
            <button
              key={h}
              onClick={() => { setHorizon(h); load(h); }}
              disabled={loading}
              style={{
                padding: '0.4rem 0.9rem',
                border: '1px solid #cbd5e0',
                borderRadius: 6,
                background: horizon === h ? 'var(--theme-primary, #1e40af)' : '#fff',
                color: horizon === h ? '#fff' : 'var(--theme-text, #1f2937)',
                fontWeight: 600,
                cursor: loading ? 'wait' : 'pointer',
              }}
            >{h}D</button>
          ))}
          <button
            onClick={() => load(horizon)}
            disabled={loading}
            style={{
              padding: '0.4rem 0.9rem',
              border: '1px solid #cbd5e0',
              borderRadius: 6,
              background: '#fff',
              color: 'var(--theme-text, #1f2937)',
              fontWeight: 600,
              cursor: loading ? 'wait' : 'pointer',
            }}
          >{loading ? 'Loading…' : 'Refresh'}</button>
        </div>
      </div>

      {error ? <div className="error-banner" style={{ marginBottom: '0.6rem' }}>{error}</div> : null}

      {/* KPI strip */}
      {summary ? (
        <div style={{ display: 'flex', gap: '0.6rem', flexWrap: 'wrap', marginBottom: '0.4rem' }}>
          <div style={kpiCardStyle}>
            <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Bookings (last 12 months)</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--theme-primary)' }}>{fmtNum(summary.total_bookings_12m)}</div>
          </div>
          <div style={kpiCardStyle}>
            <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Revenue (last 12 months)</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700, color: 'var(--theme-success, #166534)' }}>{fmtCurrency(summary.total_revenue_12m)}</div>
          </div>
          <div style={kpiCardStyle}>
            <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Avg revenue / booking</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{fmtCurrency(summary.avg_revenue_per_booking)}</div>
          </div>
          <div style={kpiCardStyle}>
            <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Halls</div>
            <div style={{ fontSize: '1.4rem', fontWeight: 700 }}>{Array.isArray(summary.halls) ? summary.halls.length : 0}</div>
          </div>
          {trendBadge ? (
            <div style={{ ...kpiCardStyle, background: trendBadge.bg, borderColor: trendBadge.color, color: trendBadge.color }}>
              <div style={{ fontSize: '0.78rem', opacity: 0.8 }}>Search interest</div>
              <div style={{ fontSize: '1.05rem', fontWeight: 700 }}>{trendBadge.label}</div>
              <div style={{ fontSize: '0.78rem', opacity: 0.85 }}>Index: {fmtNum(summary.trend_interest)} · Boosted days: {fmtNum(summary.trend_boosted_days)}</div>
            </div>
          ) : null}
        </div>
      ) : null}

      {/* Demand level summary chips */}
      {summary ? (
        <div style={{ ...sectionStyle, display: 'flex', gap: '0.7rem', alignItems: 'center', flexWrap: 'wrap', marginTop: '0.7rem' }}>
          <span style={{ fontWeight: 600, color: 'var(--theme-text-secondary, #475569)' }}>Next {horizon} days:</span>
          {[
            { key: 'high_demand_days', label: 'High demand', level: 'HIGH' },
            { key: 'medium_demand_days', label: 'Medium', level: 'MEDIUM' },
            { key: 'low_demand_days', label: 'Low', level: 'LOW' },
          ].map((s) => {
            const c = demandColor(s.level);
            return (
              <span key={s.key} style={{ background: c.bg, color: c.text, border: `1px solid ${c.border}`, padding: '0.25rem 0.7rem', borderRadius: 6, fontSize: '0.85rem', fontWeight: 600 }}>
                {s.label}: {fmtNum(summary[s.key])} day{Number(summary[s.key]) === 1 ? '' : 's'}
              </span>
            );
          })}
        </div>
      ) : null}

      {/* Two-pane layout: heatmap calendar on left, day-detail panel on right */}
      <div style={{ display: 'grid', gridTemplateColumns: selectedCell && !isMobile ? '1fr 320px' : '1fr', gap: '0.8rem', marginTop: '0.7rem' }}>
        {/* Heatmap calendar */}
        <div style={sectionStyle}>
          <div style={{ fontWeight: 700, color: 'var(--theme-primary)', marginBottom: '0.5rem' }}>Demand calendar</div>
          {loading && !data ? (
            <div style={{ color: '#6b7280' }}>Loading forecast…</div>
          ) : forecast.length === 0 ? (
            <div style={{ color: '#6b7280' }}>No forecast data available. Once you have at least a few months of bookings, the engine can predict demand.</div>
          ) : (
            calendarMonths.map(({ ym, days }) => (
              <div key={ym} style={{ marginBottom: '1rem' }}>
                <div style={{ fontWeight: 600, color: '#374151', marginBottom: '0.4rem', fontSize: '0.92rem' }}>
                  {(() => {
                    const d = new Date(`${ym}-01T00:00:00`);
                    return d.toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
                  })()}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(96px, 1fr))', gap: '0.4rem' }}>
                  {days.map((d) => {
                    const c = demandColor(d.demand);
                    const isSelected = selectedCell?.date === d.date;
                    return (
                      <button
                        key={d.date}
                        onClick={() => setSelectedCell(d)}
                        title={`${d.date} — ${d.demand}`}
                        style={{
                          background: c.bg,
                          border: `${isSelected ? '2px solid var(--theme-primary)' : `1px solid ${c.border}`}`,
                          color: c.text,
                          borderRadius: 6,
                          padding: '0.4rem 0.5rem',
                          textAlign: 'left',
                          cursor: 'pointer',
                          minHeight: 56,
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 2,
                        }}
                      >
                        <div style={{ fontSize: '0.72rem', fontWeight: 600, opacity: 0.8 }}>
                          {d.day_of_week} · {String(d.date).slice(8, 10)}
                        </div>
                        <div style={{ fontSize: '0.85rem', fontWeight: 700 }}>{d.demand}</div>
                        <div style={{ fontSize: '0.68rem', display: 'flex', gap: 4 }}>
                          {d.has_local_event ? <span title="Local event">🎉</span> : null}
                          {d.trend_boosted ? <span title="Trend-boosted">📈</span> : null}
                          {Number(d.historical_bookings) > 0 ? <span style={{ opacity: 0.85 }}>·{d.historical_bookings}</span> : null}
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Day detail */}
        {selectedCell ? (
          <div style={sectionStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
              <div style={{ fontWeight: 700, color: 'var(--theme-primary)' }}>Day detail</div>
              <button onClick={() => setSelectedCell(null)} style={{ border: 'none', background: 'none', cursor: 'pointer', fontSize: '1.1rem', color: '#6b7280' }} title="Close">×</button>
            </div>
            <div style={{ marginBottom: '0.4rem' }}>
              <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Date</div>
              <div style={{ fontWeight: 600 }}>{fmtDateLong(selectedCell.date)} ({selectedCell.day_of_week})</div>
            </div>
            <div style={{ marginBottom: '0.4rem' }}>
              <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Demand level</div>
              {(() => {
                const c = demandColor(selectedCell.demand);
                return (
                  <span style={{ display: 'inline-block', background: c.bg, color: c.text, border: `1px solid ${c.border}`, padding: '0.2rem 0.6rem', borderRadius: 6, fontWeight: 700 }}>
                    {selectedCell.demand}
                  </span>
                );
              })()}
            </div>
            <div style={{ marginBottom: '0.4rem' }}>
              <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Historical bookings (same month + day-of-week)</div>
              <div style={{ fontWeight: 600 }}>{fmtNum(selectedCell.historical_bookings)}</div>
            </div>
            <div style={{ marginBottom: '0.4rem' }}>
              <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Search trend boost</div>
              <div style={{ fontWeight: 600 }}>{selectedCell.trend_boosted ? 'Yes — search interest is lifting this day' : 'No'}</div>
            </div>
            {Array.isArray(selectedCell.local_events) && selectedCell.local_events.length > 0 ? (
              <div style={{ marginBottom: '0.4rem' }}>
                <div style={{ fontSize: '0.78rem', color: '#6b7280' }}>Local events on / around this day</div>
                <ul style={{ margin: '0.2rem 0 0 1rem', padding: 0 }}>
                  {selectedCell.local_events.map((ev, i) => (
                    <li key={i} style={{ fontSize: '0.85rem' }}>
                      <span style={{ fontWeight: 600 }}>{ev.event_name || 'Event'}</span>
                      {ev.start_date ? <span style={{ color: '#6b7280' }}> — {ev.start_date}{ev.end_date && ev.end_date !== ev.start_date ? ` to ${ev.end_date}` : ''}</span> : null}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
        ) : null}
      </div>

      <div style={{ marginTop: '0.8rem', fontSize: '0.78rem', color: '#6b7280' }}>
        Forecast generated from your past 12 months of banquet bookings, the local-events calendar, and Google Trends search interest. Refreshes on demand. The engine bins history by (month, day-of-week) and adjusts for trend momentum.
      </div>
    </div>
  );
}

export default BanquetDemandForecast;
